import "./styles.css";
import React, { useState, useEffect, useRef } from "react";
import {
  PlayerPosMap,
  PlayerPosMap23,
  PlayerPosMapNoPos,
  slotcodes,
  UNIVERSAL_VIG,
} from "./constants";
import SangTable from "./SangTable";
import {
  isFetchable,
  getLastElementMap,
  calculateLatestChange,
  getQueryStringValue,
} from "./util";
import MissingTable from "./MissingTable";
import ThemeToggleDropdown from "./ThemeToggleDropdown";
import TradeOptimizerPromo from "./TradeOptimizerPromo";
import { computeBPProjections } from "./bpProjections";
import { fetchEspnWeekStats } from "./espn/espnActuals";

function TotalContainer() {
  const [selectedPosition, setSelectedPosition] = useState(0);
  const [playerList, setPlayerList] = useState([]);
  // Live ESPN actual/projected points for the selected week, keyed by
  // normalized player name (see espn/espnActuals.js).
  const [espnMap, setEspnMap] = useState(new Map());
  const espnRequestId = useRef(0);
  const [allMap, setAllMap] = useState(new Map());
  const [recentMap, setRecentMap] = useState(new Map());
  const [selectedMode, setSelectedMode] = useState(0);
  const [selectedWeek, setSelectedWeek] = useState(9);
  const [selectedYear, setSelectedYear] = useState(2026);
  const [selectedTheme, setSelectedTheme] = useState(1);
  const [playerMissingList, setPlayerMissingList] = useState([]);
  // Index of the newest BettingPros snapshot loaded for the week.
  const [bpLastIndex, setBpLastIndex] = useState(null);
  const [bpLastFetched, setBpLastFetched] = useState(null);
  const [apiSource, setApiSource] = useState(0);
  // Fantasy points per passing TD: 4 (default) or 6.
  const [passTdPoints, setPassTdPoints] = useState(4);

  // var skewness = require('compute-skewness');
  // console.log(skewness([24.5, 15.2, 5.4, 14.9, 9.6, 21.4, 14.2, 43, 19.5]))

  const handleClick = () => {
    window.open("https://venmo.com/sanghan", "_blank", "noopener,noreferrer");
  };

  const handleTradeClick = () => {
    window.location.href = "/tradeValues";
  };

  const scrapeAllActualEspnStats = async (tempWeek) => {
    let otherMap = new Map();
    let weekIndex = 1;
    while (weekIndex < tempWeek) {
      await fetch(`./${selectedYear}/week${weekIndex}hppr`)
        .then((response) => {
          return response.json();
        })
        .then((r) => {
          let data = [];
          const d = r;
          // console.log(d);
          for (const week of d.schedule) {
            // console.log(week);
            if (weekIndex == week.matchupPeriodId) {
              let allPlayers = week.away.rosterForCurrentScoringPeriod.entries
                .slice()
                .concat(week.home.rosterForMatchupPeriod.entries.slice());

              // const tmid = tm.id;
              for (const p of allPlayers) {
                let name = p.playerPoolEntry.player.fullName;
                const slot = p.lineupSlotId;
                const pos = slotcodes[slot];

                // Injured status (need try/catch because of D/ST)
                let inj = "NA";
                try {
                  inj = p.playerPoolEntry.player.injuryStatus;
                } catch (error) {
                  // Do nothing, leave 'NA' as the default value for injured status
                }

                // Projected/actual points
                let proj = null,
                  act = null;

                for (const stat of p.playerPoolEntry.player.stats) {
                  // if (stat.scoringPeriodId !== weekIndex-1) {
                  //   continue;
                  // }
                  if (stat.statSourceId === 0) {
                    act = stat.appliedTotal;
                  } else if (stat.statSourceId === 1) {
                    proj = stat.appliedTotal;
                  }
                }

                name = name
                  .replace(/\./g, "")
                  .replace(/ jr/i, "")
                  .replace(/ sr/i, "")
                  .replace(/ Jr/i, "");
                data.push([weekIndex, name, slot, pos, inj, proj, act]);
                // console.log([weekIndex, name, slot, pos, inj, proj, act])
                if (otherMap.has(name)) {
                  let tempPlayer = otherMap.get(name);
                  while (tempPlayer.act.length < weekIndex - 1) {
                    tempPlayer = { act: tempPlayer.act.slice().concat([null]) };
                  }
                  if (typeof act?.toFixed(2) == "undefined") {
                    otherMap.set(name, {
                      // proj: tempPlayer.proj.slice().concat([proj?.toFixed(2)]),
                      act: tempPlayer.act.slice().concat([null]),
                    });
                  } else {
                    otherMap.set(name, {
                      // proj: tempPlayer.proj.slice().concat([proj?.toFixed(2)]),
                      act: tempPlayer.act.slice().concat([act?.toFixed(2)]),
                    });
                  }
                } else {
                  let tempPlayer = {
                    // proj: [],
                    act: [],
                  };
                  while (tempPlayer.act.length < weekIndex - 1) {
                    tempPlayer = { act: tempPlayer.act.slice().concat([null]) };
                  }
                  if (typeof act?.toFixed(2) == "undefined") {
                    tempPlayer = { act: tempPlayer.act.concat([null]) };
                  } else {
                    tempPlayer = {
                      act: tempPlayer.act.concat([act?.toFixed(2)]),
                    };
                  }
                  otherMap.set(name, tempPlayer);
                }
              }
            }
          }
        })
        .catch((e) => {
          console.log(e);
          otherMap.clear();
          // increment here just in case return doesnt break out
          weekIndex = weekIndex + 1;
          return;
        });
      for (const [key, value] of otherMap.entries()) {
        if (value.act.length < weekIndex) {
          let valCopy = otherMap.get(key);
          valCopy = { act: valCopy.act.slice().concat([null]) };
          otherMap.set(key, valCopy);
        }
      }
      weekIndex = weekIndex + 1;
    }
    setAllMap(otherMap);
  };

  // ESPN actuals are loaded from ESPN's API every time the projections are
  // (same effect, same dependencies), scored with the same settings, so the
  // "ESPN Act" column always compares like with like. A response that
  // arrives after the settings changed again is dropped.
  const loadEspnActuals = async (week, mode, year, ptd) => {
    const requestId = ++espnRequestId.current;
    let next = new Map();
    try {
      next = await fetchEspnWeekStats({
        season: year,
        week,
        mode,
        passTdPoints: ptd,
      });
    } catch (e) {
      console.log(e);
    }
    if (requestId === espnRequestId.current) setEspnMap(next);
  };

  const scrapeBPData = async (pos, mode, week) => {
    const { finalList, missingList, lastIndex, lastFetched } =
      await computeBPProjections({
      pos,
      mode,
      week,
      year: selectedYear,
      passTdPoints,
    });
    setPlayerMissingList(missingList);
    setBpLastIndex(lastIndex);
    setBpLastFetched(lastFetched);
    setPlayerList(finalList);
  };

  const scrapeData = async (pos, mode, week) => {
    let receptionMultiplier = 0.5;

    if (mode == 0) receptionMultiplier = 0.5;
    else if (mode == 1) receptionMultiplier = 0;
    else if (mode == 2) receptionMultiplier = 1;

    //https://www.bovada.lv/services/sports/event/v2/events/A/description/football/nfl

    let testedInts = 0;
    let lastTestedInt = 0;
    let isNewBovadaFileCheck = false;

    let bovadaFileLoopFlag = true;

    let playerToAnyTDDataPoints = new Map();
    let playerToRushYdsDataPoints = new Map();
    let playerToRushRecYdsDataPoints = new Map();
    let playerToRecYdsDataPoints = new Map();
    let playerToRecsDataPoints = new Map();
    let playerToPassTDDataPoints = new Map();
    let playerToPassYdsDataPoints = new Map();
    let playerToIntsDataPoints = new Map();
    let yearPrefix = selectedYear != 2023 ? selectedYear : "";

    while (bovadaFileLoopFlag) {
      if (testedInts > lastTestedInt) {
        isNewBovadaFileCheck = true;
        lastTestedInt++;
      }

      // await fetch(
      //   "https://raw.githubusercontent.com/seoularpro/OddsVis/main/FanduelFiles/" +
      //   yearPrefix +
      //   "week" +
      //   week +
      //   "" +
      //   testedInts
      // ).then((response) => {
      //   return response.json();
      // }).then((data) => {
      //   console.log(data);
      // })

      await fetch(
        "https://raw.githubusercontent.com/seoularpro/OddsVis/main/BovadaAPIFiles/" +
          yearPrefix +
          "week" +
          week +
          "" +
          testedInts
      )
        .then((response) => {
          return response.json();
        })
        .then((data) => {
          let allNflGames = data[0].events.slice();

          for (let i = 0; i < allNflGames.length; i++) {
            let eachGameTDOutcomes = allNflGames[i].displayGroups
              .find((x) => x.id == "100-1870")
              ?.markets.find(
                (y) =>
                  y.descriptionKey == "Anytime Touchdown Scorer" &&
                  y.period.id == "119"
              )?.outcomes;
            let eachGameRushingOutcomes = allNflGames[i].displayGroups
              .find((x) => x.id == "100-93")
              ?.markets.filter((y) => y.marketTypeId == "121337");
            let eachGameRushingAndReceivingOutcomes = allNflGames[
              i
            ].displayGroups
              .find((x) => x.id == "100-93")
              ?.markets.filter((y) => y.marketTypeId == "121328");
            let eachGameReceivingOutcomes = allNflGames[i].displayGroups
              .find((x) => x.id == "100-94")
              ?.markets.filter((y) => y.marketTypeId == "121333");
            let eachGameReceptionOutcomes = allNflGames[i].displayGroups
              .find((x) => x.id == "100-94")
              ?.markets.filter((y) => y.marketTypeId == "121332");
            let eachGamePassingYdOutcomes;
            let eachGamePassingTDOutcomes;
            let eachGameIntOutcomes;
            if (selectedYear == 2023) {
              eachGamePassingYdOutcomes = allNflGames[i].displayGroups
                .find((x) => x.id == "100-1188")
                ?.markets.filter((y) => y.marketTypeId == "121348");
              eachGamePassingTDOutcomes = allNflGames[i].displayGroups
                .find((x) => x.id == "100-1188")
                ?.markets.filter((y) => y.marketTypeId == "121335");
              eachGameIntOutcomes = allNflGames[i].displayGroups
                .find((x) => x.id == "100-1188")
                ?.markets.filter((y) => y.marketTypeId == "121329");
            } else {
              eachGamePassingYdOutcomes = allNflGames[i].displayGroups
                .find((x) => x.id == "100-1932")
                ?.markets.filter((y) => y.marketTypeId == "121348");
              eachGamePassingTDOutcomes = allNflGames[i].displayGroups
                .find((x) => x.id == "100-1932")
                ?.markets.filter((y) => y.marketTypeId == "121335");
              eachGameIntOutcomes = allNflGames[i].displayGroups
                .find((x) => x.id == "100-1932")
                ?.markets.filter((y) => y.marketTypeId == "121329");
            }

            if (typeof eachGameTDOutcomes !== "undefined") {
              let amonRaFlag = false;
              for (let j = 0; j < eachGameTDOutcomes.length; j++) {
                let playerOdds = eachGameTDOutcomes[j];

                if (
                  playerOdds.description == "Amon-Ra St.Brown" ||
                  playerOdds.description == "Amon-Ra St. Brown"
                ) {
                  playerOdds.description = "Amon-Ra St. Brown";
                }
                playerOdds.description = playerOdds.description
                  .replace(/\./g, "")
                  .replace(/ jr/i, "")
                  .replace(/ sr/i, "")
                  .replace(/ Jr/i, "");

                if (playerOdds.description == "AJ Brown ") {
                  playerOdds.description = playerOdds.description.slice(0, -1);
                }
                if (playerOdds.description == "Gardner Minshew") {
                  playerOdds.description = "Gardner Minshew II";
                }
                if (playerOdds.description == "Devon Achane (MIA)") {
                  playerOdds.description = "De'Von Achane (MIA)";
                }
                if (playerOdds.description == "Brian Robinson") {
                  playerOdds.description = "Brian Robinson (WAS)";
                }
                if (playerOdds.description == "D'Andre Swift") {
                  playerOdds.description = "D'Andre Swift (CHI)";
                }
                if (playerOdds.description == "Gabriel Davis (JAX)") {
                  playerOdds.description = "Gabe Davis (JAX)";
                }
                if (playerOdds.description == "Rome Odunze (CHI) ") {
                  playerOdds.description = playerOdds.description.slice(0, -1);
                }
                if (playerOdds.description == "Chigoziem Okonkwo (TEN)") {
                  playerOdds.description = "Chig Okonkwo (TEN)";
                }

                if (!amonRaFlag) {
                  let newAnyTDList = [];
                  if (playerToAnyTDDataPoints.has(playerOdds.description)) {
                    newAnyTDList = playerToAnyTDDataPoints
                      .get(playerOdds.description)
                      .slice();
                  }
                  newAnyTDList.push(
                    (1 / playerOdds.price.decimal / UNIVERSAL_VIG) * 6
                  );
                  playerToAnyTDDataPoints.set(
                    playerOdds.description,
                    newAnyTDList
                  );
                }

                if (name == "Amon-Ra St. Brown") {
                  amonRaFlag = true;
                }
              }
            }
            if (typeof eachGameRushingAndReceivingOutcomes !== "undefined") {
              let amonRaFlag = false;
              for (
                let j = 0;
                j < eachGameRushingAndReceivingOutcomes.length;
                j++
              ) {
                let playerOdds = eachGameRushingAndReceivingOutcomes[j];
                let name = playerOdds.description.slice(34);
                if (name == "Amon-Ra St.Brown" || name == "Amon-Ra St. Brown") {
                  name = "Amon-Ra St. Brown";
                }
                name = name
                  .replace(/\./g, "")
                  .replace(/ jr/i, "")
                  .replace(/ sr/i, "");
                if (name == "AJ Brown ") {
                  name = name.slice(0, -1);
                }
                if (name == "Deebo Samuel") {
                  name = "Deebo Samuel (SF)";
                }

                if (!amonRaFlag) {
                  let newRushRecYdsList = [];
                  if (playerToRushRecYdsDataPoints.has(name)) {
                    newRushRecYdsList = playerToRushRecYdsDataPoints.get(name);
                  }
                  newRushRecYdsList.push(
                    playerOdds.outcomes[0].price.handicap / 10
                  );
                  playerToRushRecYdsDataPoints.set(name, newRushRecYdsList);
                }
                if (name == "Amon-Ra St. Brown") {
                  amonRaFlag = true;
                }
              }
            }
            if (typeof eachGameRushingOutcomes !== "undefined") {
              let amonRaFlag = false;
              for (let j = 0; j < eachGameRushingOutcomes.length; j++) {
                let playerOdds = eachGameRushingOutcomes[j];
                let name = playerOdds.description.slice(22);
                if (name == "Amon-Ra St.Brown" || name == "Amon-Ra St. Brown") {
                  name = "Amon-Ra St. Brown";
                }
                name = name
                  .replace(/\./g, "")
                  .replace(/ jr/i, "")
                  .replace(/ sr/i, "");
                if (name == "AJ Brown ") {
                  name = name.slice(0, -1);
                }
                if (name == "Deebo Samuel") {
                  name = "Deebo Samuel (SF)";
                }

                if (!amonRaFlag) {
                  let newRushYdsList = [];
                  if (playerToRushYdsDataPoints.has(name)) {
                    newRushYdsList = playerToRushYdsDataPoints.get(name);
                  }
                  newRushYdsList.push(
                    playerOdds.outcomes[0].price.handicap / 10
                  );
                  playerToRushYdsDataPoints.set(name, newRushYdsList);
                }
                if (name == "Amon-Ra St. Brown") {
                  amonRaFlag = true;
                }
              }
            }
            if (typeof eachGameReceivingOutcomes !== "undefined") {
              let amonRaFlag = false;
              for (let j = 0; j < eachGameReceivingOutcomes.length; j++) {
                let playerOdds = eachGameReceivingOutcomes[j];
                let name = playerOdds.description.slice(24);
                if (name == "Amon-Ra St.Brown" || name == "Amon-Ra St. Brown") {
                  name = "Amon-Ra St. Brown";
                }

                name = name
                  .replace(/\./g, "")
                  .replace(/ jr/i, "")
                  .replace(/ sr/i, "");
                if (name == "AJ Brown ") {
                  name = name.slice(0, -1);
                }
                if (name == "Deebo Samuel") {
                  name = "Deebo Samuel (SF)";
                }

                if (!amonRaFlag) {
                  let newRecYdsList = [];
                  if (playerToRecYdsDataPoints.has(name)) {
                    newRecYdsList = playerToRecYdsDataPoints.get(name);
                  }
                  newRecYdsList.push(
                    playerOdds.outcomes[0].price.handicap / 10
                  );
                  playerToRecYdsDataPoints.set(name, newRecYdsList);
                }
                if (name == "Amon-Ra St. Brown") {
                  amonRaFlag = true;
                }
              }
            }
            if (typeof eachGameReceptionOutcomes !== "undefined") {
              let amonRaFlag = false;

              for (let j = 0; j < eachGameReceptionOutcomes.length; j++) {
                let playerOdds = eachGameReceptionOutcomes[j];
                let name = playerOdds.description.slice(19);
                if (name == "Amon-Ra St.Brown" || name == "Amon-Ra St. Brown") {
                  name = "Amon-Ra St. Brown";
                }
                name = name
                  .replace(/\./g, "")
                  .replace(/ jr/i, "")
                  .replace(/ sr/i, "");
                if (name == "Deebo Samuel") {
                  name = "Deebo Samuel (SF)";
                }
                if (name == "AJ Brown ") {
                  name = name.slice(0, -1);
                }
                if (!amonRaFlag) {
                  let newRecsList = [];
                  if (playerToRecsDataPoints.has(name)) {
                    newRecsList = playerToRecsDataPoints.get(name);
                  }
                  let handicap = playerOdds.outcomes[0].price.handicap;
                  handicap =
                    handicap -
                    0.5 +
                    1 / playerOdds.outcomes[0].price.decimal / UNIVERSAL_VIG;
                  newRecsList.push(handicap * receptionMultiplier);
                  playerToRecsDataPoints.set(name, newRecsList);
                }

                if (name == "Amon-Ra St. Brown") {
                  amonRaFlag = true;
                }
              }
            }
            if (typeof eachGamePassingYdOutcomes !== "undefined") {
              for (let j = 0; j < eachGamePassingYdOutcomes.length; j++) {
                let playerOdds = eachGamePassingYdOutcomes[j];
                let name = playerOdds.description.slice(22);
                name = name
                  .replace(/\./g, "")
                  .replace(/ jr/i, "")
                  .replace(/ sr/i, "");
                if (name == "AJ Brown ") {
                  name = name.slice(0, -1);
                }

                let newPassYdsList = [];
                if (playerToPassYdsDataPoints.has(name)) {
                  newPassYdsList = playerToPassYdsDataPoints.get(name);
                }
                newPassYdsList.push(playerOdds.outcomes[0].price.handicap / 25);
                playerToPassYdsDataPoints.set(name, newPassYdsList);
              }
            }
            if (typeof eachGamePassingTDOutcomes !== "undefined") {
              for (let j = 0; j < eachGamePassingTDOutcomes.length; j++) {
                let playerOdds = eachGamePassingTDOutcomes[j];
                let name = playerOdds.description.slice(27);
                name = name
                  .replace(/\./g, "")
                  .replace(/ jr/i, "")
                  .replace(/ sr/i, "");
                if (name == "AJ Brown ") {
                  name = name.slice(0, -1);
                }

                let newPassTdsList = [];
                if (playerToPassTDDataPoints.has(name)) {
                  newPassTdsList = playerToPassTDDataPoints.get(name);
                }
                let handicap = playerOdds.outcomes[0].price.handicap;
                handicap =
                  handicap -
                  0.5 +
                  1 / playerOdds.outcomes[0].price.decimal / UNIVERSAL_VIG;
                newPassTdsList.push(handicap * passTdPoints);
                playerToPassTDDataPoints.set(name, newPassTdsList);
              }
            }
            if (typeof eachGameIntOutcomes !== "undefined") {
              for (let j = 0; j < eachGameIntOutcomes.length; j++) {
                let playerOdds = eachGameIntOutcomes[j];
                let name = playerOdds.description.slice(29);
                name = name
                  .replace(/\./g, "")
                  .replace(/ jr/i, "")
                  .replace(/ sr/i, "");
                if (name == "AJ Brown ") {
                  name = name.slice(0, -1);
                }

                let newIntsList = [];

                if (playerToIntsDataPoints.has(name)) {
                  newIntsList = playerToIntsDataPoints.get(name);
                }
                let handicap = playerOdds.outcomes[0].price.handicap;
                handicap =
                  handicap -
                  0.5 +
                  1 / playerOdds.outcomes[0].price.decimal / UNIVERSAL_VIG;
                newIntsList.push(handicap * -2);
                playerToIntsDataPoints.set(name, newIntsList);
              }
            }
          }
        })
        .catch((e) => {});

      testedInts++;
      isNewBovadaFileCheck = false;
      bovadaFileLoopFlag = await isFetchable(
        "https://raw.githubusercontent.com/seoularpro/OddsVis/main/BovadaAPIFiles/" +
          yearPrefix +
          "week" +
          week +
          "" +
          testedInts
      );
    }

    // playerToRecYdsDataPoints.set("De'Von Achane (MIA)", [2.85]);
    // playerToRecsDataPoints.set("James Cook (BUF)", [1.25]);
    // playerToRecsDataPoints.set("Travis Etienne (JAX)", [1.675]);
    // playerToRecsDataPoints.set("Jerome Ford (CLE)", [1.28]);
    // playerToRecsDataPoints.set("Zach Charbonnet (SEA)", [1.33]);

    let playerToAnyTD = getLastElementMap(playerToAnyTDDataPoints);
    let playerToRushYds = getLastElementMap(playerToRushYdsDataPoints);
    let playerToRushRecYds = getLastElementMap(playerToRushRecYdsDataPoints);
    let playerToRecYds = getLastElementMap(playerToRecYdsDataPoints);
    let playerToRecs = getLastElementMap(playerToRecsDataPoints);
    let playerToPassTD = getLastElementMap(playerToPassTDDataPoints);
    let playerToPassYds = getLastElementMap(playerToPassYdsDataPoints);
    let playerToInts = getLastElementMap(playerToIntsDataPoints);

    let latestCPlayerToAnyTD = calculateLatestChange(playerToAnyTDDataPoints);
    let latestCPlayerToRushYds = calculateLatestChange(
      playerToRushYdsDataPoints
    );
    let latestCPlayerToRushRecYds = calculateLatestChange(
      playerToRushRecYdsDataPoints
    );
    let latestCPlayerToRecYds = calculateLatestChange(playerToRecYdsDataPoints);
    let latestCPlayerToRecs = calculateLatestChange(playerToRecsDataPoints);
    let latestCPlayerToPassTD = calculateLatestChange(playerToPassTDDataPoints);
    let latestCPlayerToPassYds = calculateLatestChange(
      playerToPassYdsDataPoints
    );
    let latestCPlayerToInts = calculateLatestChange(playerToIntsDataPoints);

    let finalPlayerToEV = new Map();
    let finalPlayerToDPCount = new Map();
    let finalCPlayer = new Map();
    function sumPlayerEVs() {
      Array.from(arguments).forEach((arg) => {
        arg.forEach((value, key) => {
          let temp = value;
          if (finalPlayerToEV.has(key)) {
            temp = finalPlayerToEV.get(key);
            temp += value;
          }
          finalPlayerToEV.set(key, temp);
        });
      });
    }
    function sumPlayerChanges() {
      Array.from(arguments).forEach((arg) => {
        arg.forEach((value, key) => {
          let temp = value;
          if (finalCPlayer.has(key)) {
            temp = finalCPlayer.get(key);
            temp += value;
          }
          finalCPlayer.set(key, temp);
        });
      });
    }
    function sumPlayerDP() {
      Array.from(arguments).forEach((arg) => {
        arg.forEach((value, key) => {
          let temp = 1;
          if (finalPlayerToDPCount.has(key)) {
            temp = finalPlayerToDPCount.get(key);
            temp++;
          }
          finalPlayerToDPCount.set(key, temp);
        });
      });
    }
    sumPlayerEVs(
      playerToAnyTD,
      playerToRushYds,
      playerToRecYds,
      playerToRecs,
      playerToPassTD,
      playerToPassYds,
      playerToInts
    );
    sumPlayerDP(
      playerToAnyTD,
      playerToRushYds,
      playerToRecYds,
      playerToRecs,
      playerToPassTD,
      playerToPassYds,
      playerToInts
    );
    sumPlayerChanges(
      latestCPlayerToAnyTD,
      latestCPlayerToRushYds,
      latestCPlayerToRecYds,
      latestCPlayerToRecs,
      latestCPlayerToPassTD,
      latestCPlayerToPassYds,
      latestCPlayerToInts
    );

    const mapEntries = Array.from(finalPlayerToEV.entries());
    // Sort the array based on the numeric value (assuming values are numbers)
    mapEntries.sort((a, b) => b[1] - a[1]);
    // Create a new Map from the sorted array
    const sortedMap = new Map(mapEntries);
    let finalList;
    if (selectedYear == 2023) {
      finalList = Array.from(sortedMap.entries()).filter(
        (x) =>
          typeof PlayerPosMap23.get(x[0]) !== "undefined" &&
          (PlayerPosMap23.get(x[0]) == pos ||
            pos == 99 ||
            (pos == 98 && PlayerPosMap23.get(x[0]) !== 0)) &&
          x[1] > 1
      );
    } else {
      finalList = Array.from(sortedMap.entries()).filter(
        (x) =>
          typeof PlayerPosMap.get(x[0]) !== "undefined" &&
          (PlayerPosMap.get(x[0]) == pos ||
            pos == 99 ||
            (pos == 98 && PlayerPosMap.get(x[0]) !== 0)) &&
          x[1] > 1
      );
    }
    let replacedRushRecFlag = false;
    let missingList = [];
    if (pos == 0) {
      finalList = finalList.filter((d) => {
        let qbHasAllValues =
          playerToAnyTD.has(d[0]) &&
          playerToRushYds.has(d[0]) &&
          playerToPassTD.has(d[0]) &&
          playerToPassYds.has(d[0]) &&
          playerToInts.has(d[0]);

        if (!qbHasAllValues) {
          let qbMessage = "";
          if (!playerToAnyTD.has(d[0])) {
            qbMessage = qbMessage.concat(" AnyTD ");
          }
          if (!playerToRushYds.has(d[0])) {
            qbMessage = qbMessage.concat(" RushYds ");
          }
          if (!playerToPassTD.has(d[0])) {
            qbMessage = qbMessage.concat(" PassTDs ");
          }
          if (!playerToPassYds.has(d[0])) {
            qbMessage = qbMessage.concat(" PassYds ");
          }
          if (!playerToInts.has(d[0])) {
            qbMessage = qbMessage.concat(" Ints ");
          }
          missingList.push([d[0], qbMessage, d[1].toFixed(2)]);
        }
        return qbHasAllValues;
      });
    } else if (pos == 1) {
      finalList = finalList.filter((d, di) => {
        let rbHasAllValues =
          playerToAnyTD.has(d[0]) &&
          playerToRushYds.has(d[0]) &&
          playerToRecYds.has(d[0]) &&
          playerToRecs.has(d[0]);
        if (!rbHasAllValues) {
          if (
            (playerToAnyTD.has(d[0]) &&
              !playerToRushYds.has(d[0]) &&
              playerToRecYds.has(d[0]) &&
              playerToRecs.has(d[0])) ||
            (playerToAnyTD.has(d[0]) &&
              playerToRushYds.has(d[0]) &&
              !playerToRecYds.has(d[0]) &&
              playerToRecs.has(d[0])) ||
            (playerToAnyTD.has(d[0]) &&
              !playerToRushYds.has(d[0]) &&
              !playerToRecYds.has(d[0]) &&
              playerToRecs.has(d[0]))
          ) {
            if (playerToRushRecYdsDataPoints.has(d[0])) {
              if (playerToRushYds.has(d[0])) {
                finalList[di][1] = finalList[di][1] - playerToRushYds.get(d[0]);
              }
              if (playerToRecYds.has(d[0])) {
                finalList[di][1] = finalList[di][1] - playerToRecYds.get(d[0]);
              }
              finalList[di][1] =
                finalList[di][1] + playerToRushRecYds.get(d[0]);
              replacedRushRecFlag = true;
              return true;
            }
          }
          let rbMessage = "";
          if (!playerToAnyTD.has(d[0])) {
            rbMessage = rbMessage.concat(" AnyTD ");
          }
          if (!playerToRushYds.has(d[0])) {
            rbMessage = rbMessage.concat(" RushYds ");
          }
          if (!playerToRecYds.has(d[0])) {
            rbMessage = rbMessage.concat(" RecYds ");
          }
          if (!playerToRecs.has(d[0])) {
            rbMessage = rbMessage.concat(" Recs ");
          }
          missingList.push([d[0], rbMessage, d[1].toFixed(2)]);
        }
        return rbHasAllValues;
      });
    } else if (pos == 2 || pos == 3) {
      finalList = finalList.filter((d) => {
        let WRHasAllValues =
          playerToAnyTD.has(d[0]) &&
          playerToRecYds.has(d[0]) &&
          playerToRecs.has(d[0]);
        if (!WRHasAllValues) {
          let wrteMessage = "";
          if (!playerToAnyTD.has(d[0])) {
            wrteMessage = wrteMessage.concat(" AnyTD ");
          }
          if (!playerToRecYds.has(d[0])) {
            wrteMessage = wrteMessage.concat(" RecYds ");
          }
          if (!playerToRecs.has(d[0])) {
            wrteMessage = wrteMessage.concat(" Recs ");
          }
          missingList.push([d[0], wrteMessage, d[1].toFixed(2)]);
        }

        return WRHasAllValues;
      });
    } else if (pos == 98) {
      finalList = finalList.filter((d) => {
        let flexHasAllValues =
          (playerToAnyTD.has(d[0]) &&
            playerToRecYds.has(d[0]) &&
            playerToRecs.has(d[0])) ||
          (playerToAnyTD.has(d[0]) &&
            playerToRushYds.has(d[0]) &&
            playerToRecYds.has(d[0]) &&
            playerToRecs.has(d[0]));
        return flexHasAllValues;
      });
    } else if (pos == 99) {
      finalList = finalList.filter((d) => {
        let flexHasAllValues =
          (playerToAnyTD.has(d[0]) &&
            playerToRecYds.has(d[0]) &&
            playerToRecs.has(d[0])) ||
          (playerToAnyTD.has(d[0]) &&
            playerToRushYds.has(d[0]) &&
            playerToRecYds.has(d[0]) &&
            playerToRecs.has(d[0])) ||
          (playerToAnyTD.has(d[0]) &&
            playerToRushYds.has(d[0]) &&
            playerToPassTD.has(d[0]) &&
            playerToPassYds.has(d[0]) &&
            playerToInts.has(d[0]));

        return flexHasAllValues;
      });
    }

    finalList = finalList.slice();
    finalList = finalList.sort((a, b) => b[1] - a[1]);

    finalList = finalList.filter((elem) => {
      return elem[1] > 5;
    });

    finalList = finalList.map((elem) => {
      return [
        elem[0],
        {
          ev: elem[1],
          change: finalCPlayer.get(elem[0]),
        },
      ];
    });
    setPlayerMissingList(missingList);
    setPlayerList(finalList);
  };
  useEffect(() => {
    if (apiSource == 0) {
      scrapeBPData(selectedPosition, selectedMode, selectedWeek).catch(
        console.error
      );
    } else {
      scrapeData(selectedPosition, selectedMode, selectedWeek).catch(
        console.error
      );
    }
    loadEspnActuals(selectedWeek, selectedMode, selectedYear, passTdPoints);

    // scrapeAllActualEspnStats(selectedWeek)
  }, [
    selectedPosition,
    selectedMode,
    selectedWeek,
    selectedYear,
    apiSource,
    passTdPoints,
  ]);

  useEffect(() => {
    scrapeBPData(selectedPosition, selectedMode, selectedWeek).catch(
      console.error
    );
    scrapeAllActualEspnStats(selectedWeek);
  }, []);

  const redirectToPatreon = () => {
    window.location.href = "https://www.patreon.com/VegasLytics";
  };

  const positionLabels = {
    0: "QB",
    1: "RB",
    2: "WR",
    3: "TE",
    98: "FLEX",
    99: "SUPERFLEX",
  };
  const scoringLabels = { 0: "Half PPR", 1: "Standard", 2: "Full PPR" };
  const providerLabels = { 0: "Consensus", 1: "Bovada" };

  return (
    <div className="vl-page">
      <TradeOptimizerPromo />

      <div className="vl-page-head">
        <div>
          <h1 className="vl-title">Weekly Fantasy Projections</h1>
          <p className="vl-subtitle">
            Median fantasy point projections modeled from live Vegas player props.
          </p>
        </div>
      </div>

      <div className="vl-toolbar" role="group" aria-label="Projection filters">
        <div className="vl-toolbar-group">
          <div className="vl-field">
            <label className="vl-label" htmlFor="posSelect">Position</label>
            <select
              id="posSelect"
              className="vl-select"
              defaultValue={selectedPosition}
              onChange={(e) => {
                setSelectedPosition(parseInt(e.target.value));
              }}
            >
              <option value="0">QB</option>
              <option value="1">RB</option>
              <option value="2">WR</option>
              <option value="3">TE</option>
              <option value="98">FLEX</option>
              <option value="99">SUPERFLEX</option>
            </select>
          </div>
          <div className="vl-field">
            <label className="vl-label" htmlFor="scoringSelect">Scoring</label>
            <select
              id="scoringSelect"
              className="vl-select"
              defaultValue={selectedMode}
              onChange={(e) => {
                setSelectedMode(parseInt(e.target.value));
              }}
            >
              <option value="0">Half PPR</option>
              <option value="1">Standard</option>
              <option value="2">Full PPR</option>
            </select>
          </div>
          <div className="vl-field">
            <label className="vl-label" htmlFor="passTdSelect">Pass TD Pts</label>
            <select
              id="passTdSelect"
              className="vl-select"
              defaultValue={passTdPoints}
              onChange={(e) => {
                setPassTdPoints(parseInt(e.target.value));
              }}
            >
              <option value="4">4 pts</option>
              <option value="6">6 pts</option>
            </select>
          </div>
          <div className="vl-field">
            <label className="vl-label" htmlFor="yearSelect">Season</label>
            <select
              id="yearSelect"
              className="vl-select"
              defaultValue={selectedYear}
              onChange={(e) => {
                if (parseInt(e.target.value) == 2023) {
                  if (selectedWeek < 10) {
                    setSelectedWeek(18);
                    const selectElement = document.getElementById("weekSelect");
                    selectElement.value = "18";
                  }
                } else {
                  // 2026 is in progress: clamp to its latest active week.
                  // Bump this alongside the week options below each week.
                  const maxWeek = parseInt(e.target.value) == 2026 ? 9 : 1;
                  if (selectedWeek > maxWeek) {
                    setSelectedWeek(maxWeek);
                    const selectElement = document.getElementById("weekSelect");
                    selectElement.value = String(maxWeek);
                  }
                }
                setSelectedYear(parseInt(e.target.value));
              }}
            >
              <option value="2026">2026</option>
              <option value="2025">2025</option>
              <option value="2024">2024</option>
              <option disabled={apiSource == 0} value="2023">
                2023
              </option>
            </select>
          </div>
          <div className="vl-field">
            <label className="vl-label" htmlFor="weekSelect">Week</label>
            <select
              className="vl-select"
              id="weekSelect"
              defaultValue={selectedWeek}
              onChange={(e) => {
                setSelectedWeek(parseInt(e.target.value));
              }}
            >
              <option disabled={selectedYear == 2025 || selectedYear == 2026} value="18">
                Week 18
              </option>
              <option disabled={selectedYear == 2026} value="17">
                Week 17
              </option>
              <option disabled={selectedYear == 2026} value="16">Week 16</option>
              <option disabled={selectedYear == 2026} value="15">Week 15</option>
              <option disabled={selectedYear == 2026} value="14">Week 14</option>
              <option disabled={selectedYear == 2026} value="13">Week 13</option>
              <option disabled={selectedYear == 2026} value="12">Week 12</option>
              <option disabled={selectedYear == 2026} value="11">Week 11</option>
              <option disabled={selectedYear == 2026} value="10">Week 10</option>
              <option disabled={selectedYear == 2023} value="9">
                Week 9
              </option>
              <option disabled={selectedYear == 2023} value="8">
                Week 8
              </option>
              <option disabled={selectedYear == 2023} value="7">
                Week 7
              </option>
              <option disabled={selectedYear == 2023} value="6">
                Week 6
              </option>
              <option disabled={selectedYear == 2023} value="5">
                Week 5
              </option>
              <option disabled={selectedYear == 2023} value="4">
                Week 4
              </option>
              <option disabled={selectedYear == 2023} value="3">
                Week 3
              </option>
              <option disabled={selectedYear == 2023} value="2">
                Week 2
              </option>
              <option disabled={selectedYear == 2023} value="1">
                Week 1
              </option>
            </select>
          </div>
          <div className="vl-field">
            <label className="vl-label" htmlFor="providerSelect">Odds source</label>
            <select
              id="providerSelect"
              className="vl-select"
              defaultValue={apiSource}
              onChange={(e) => {
                setApiSource(parseInt(e.target.value));
              }}
            >
              <option value="0">Consensus</option>
              <option disabled={selectedYear == 2025 || selectedYear == 2026} value="1">
                Bovada
              </option>
            </select>
          </div>
          <div className="vl-field">
            <label className="vl-label" htmlFor="cellSelect">Cell style</label>
            <select
              id="cellSelect"
              className="vl-select"
              defaultValue={selectedTheme}
              onChange={(e) => {
                setSelectedTheme(parseInt(e.target.value));
              }}
            >
              <option value="0">Color</option>
              <option value="3">Translucent</option>
              <option value="4">Glass</option>
              <option value="5">Color Glass</option>
              <option value="2">Color Outline</option>
              <option value="1">Default</option>
            </select>
          </div>
          <div className="vl-field">
            <label className="vl-label" htmlFor="themeSelect">Theme</label>
            <ThemeToggleDropdown id="themeSelect" />
          </div>
        </div>
        <div className="vl-toolbar-actions">
          <button className="vl-btn vl-btn-success" onClick={handleTradeClick}>
            Trade Value Chart
          </button>
        </div>
      </div>

      <SangTable
        selectedWeek={selectedWeek}
        lastIndex={apiSource == 0 ? bpLastIndex : null}
        lastFetched={apiSource == 0 ? bpLastFetched : null}
        evList={playerList}
        selectedProvider={apiSource}
        espnPlayerMap={espnMap}
        selectedTheme={selectedTheme}
        selectedPosition={selectedPosition}
        allMap={allMap}
        recentMap={recentMap}
        mode={selectedMode}
        missingCount={playerMissingList.length}
        context={{
          position: positionLabels[selectedPosition],
          scoring:
            scoringLabels[selectedMode] +
            (passTdPoints !== 4 ? ` · ${passTdPoints}pt Pass TD` : ""),
          provider: providerLabels[apiSource],
          year: selectedYear,
        }}
      />

      <div className="vl-panel-grid vl-section">
        <div className="vl-panel">
          <div className="vl-panel-title">
            League lineup import
            <span className="vl-badge-new">New</span>
          </div>
          <p className="vl-panel-msg">
            Import your ESPN or Sleeper league to see every team's current starters with
            this week's median projections.
          </p>
          <div className="vl-panel-actions">
            <button
              className="vl-btn vl-btn-primary"
              onClick={() => {
                window.location.href = "/leagueLineups";
              }}
            >
              Import league lineups
            </button>
          </div>
        </div>

        <div className="vl-panel">
          <div className="vl-panel-title">Support VegasLytics</div>
          <p className="vl-panel-msg">
            Fund future functionality and get early access to new tools by
            subscribing.
          </p>
          <div className="vl-panel-actions">
            <button className="vl-btn vl-btn-primary" onClick={redirectToPatreon}>
              Join VLPro+
            </button>
            <button className="vl-btn vl-btn-ghost" onClick={handleClick}>
              Tip via Venmo
            </button>
          </div>
        </div>
      </div>

      <div className="vl-section">
        <MissingTable
          selectedPosition={selectedPosition}
          missingList={playerMissingList}
        />
      </div>
    </div>
  );
}

export default TotalContainer;
